import { Grid } from '@material-ui/core';
import { getDeviceCount } from '@portal/actions/device';
import {
  availablePermissions,
  createUser,
  getById,
  updatePermissions,
} from '@portal/actions/user';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import PermissionsCard from '@portal/components/PermissionsCard/PermissionsCard';

import { getRouteStackPath } from '@portal/config/routes';

import useReduxState from '@portal/hooks/useReduxState';
import * as MessageService from '~/services/message';
import * as StorageService from '~/services/storage';

import { translate } from '@portal/services/i18n';

import { PermissionsCardType } from '@portal/models/enumerators/cards';
import NodeRSA from 'node-rsa';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import AdvancedCheckbox from '@portal/components/AdvancedCheckbox/AdvancedCheckbox';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import {
  cards,
  employees,
  myAccount,
  payment,
  payslip,
  pix,
  transfer,
} from '@portal/config/app.json';
import { useNavigation } from '@portal/hooks/useNavigation';
import { AccountTaxIdentifierType } from '@portal/models/enumerators';
import { ServiceFeature } from '@portal/models/enumerators/employees';

const PermissionsMultiAccess = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation<{
    account?: models.AccountMultiAccess;
    credential?: models.CredentialMultiAccess;
    userId?: string;
  }>();
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  const {
    user: {
      createUserStatus,
      getByIdList,
      updatePermissionsStatus,
      me,
      permissionsList,
    },
    device: { deviceCount },
  } = useReduxState();

  useEffect(() => {
    if (navigate?.location?.state?.userId) {
      dispatch(getById(navigate?.location?.state?.userId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate?.location?.state?.userId]);

  useEffect(() => {
    dispatch(availablePermissions());
    dispatch(getDeviceCount());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allRolesSelected =
    permissionsList.length > 0 &&
    permissionsList.every((p) => selectedRoles.includes(p.value ?? ''));

  useEffect(() => {
    if (navigate?.location?.state?.userId) {
      if (getByIdList?.find((role) => role === 'my-account')) {
        setSelectedRoles(permissionsList.map((item) => item.value ?? ''));
        return;
      } else {
        setSelectedRoles(getByIdList);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getByIdList, permissionsList]);

  const onChange = (role: string) => {
    if (role === 'my-account') {
      if (allRolesSelected) {
        setSelectedRoles([]);
      } else {
        setSelectedRoles(permissionsList.map((item) => item.value ?? ''));
      }
      return;
    }

    setSelectedRoles((oldRoles) => {
      const withoutMyAccount = oldRoles.filter((r) => r !== 'my-account');
      return withoutMyAccount.some((selectedRole) => selectedRole === role)
        ? withoutMyAccount.filter((selectedRole) => selectedRole !== role)
        : withoutMyAccount.concat(role);
    });
  };

  const publicKey = StorageService.getItem(`publicKey_${me.id}`);

  const allowEmployees = me?.account?.financialFeature?.some(
    (account) => account.serviceType === ServiceFeature.ALLOW_PAYMENT
  );

  const hasMatchingPermissions = (type: PermissionsCardType) => {
    return permissionsList?.filter((item) => type === item.type).length > 0;
  };

  const handleSubmit = () => {
    if (
      navigate?.location?.state?.userId &&
      !navigate?.location?.state?.account
    ) {
      dispatch(
        updatePermissions(navigate?.location?.state?.userId, selectedRoles)
      );
      return;
    }
    if (
      navigate?.location?.state?.credential &&
      navigate?.location?.state?.account
    ) {
      if (
        deviceCount &&
        deviceCount.maxDevices - deviceCount.actualDevices <= 0
      ) {
        MessageService.error('PAGES.MY_DEVICES.NO_SLOTS_MULTI_ACCESS');
        return;
      }

      const keys = new NodeRSA(
        Buffer.from(publicKey ?? '', 'base64').toString('utf8')
      );
      keys.setOptions({ encryptionScheme: 'pkcs1' });
      const passwordUser = keys.encrypt(
        navigate?.location?.state?.credential?.password
      );
      const requestData: models.CreateUser = {
        user: {
          name: navigate?.location?.state?.account?.name,
          email: navigate?.location?.state?.account?.email,
          login: navigate?.location?.state?.credential?.login,
          password: passwordUser.toString('base64'),
          cellphone: `55${navigate?.location?.state?.account?.cellphone}`,
          permissions: selectedRoles,
        },
      };
      dispatch(createUser(requestData));
    }
  };

  useEffect(() => {
    if (createUserStatus || updatePermissionsStatus) {
      navigate(getRouteStackPath('PROFILE', 'MULTI_ACCESS'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createUserStatus, updatePermissionsStatus]);

  const handleBreadcrumbItems = () => {
    if (navigate?.location?.state?.account) {
      return [
        {
          active: true,
          title: translate('PAGES.HOME.PAY.BREADCRUMB.HOME'),
          url: getRouteStackPath('HOME', 'HOME_PAGE'),
        },
        {
          active: true,
          title: translate('PAGES.MY_ACCOUNT.TABS.ACCOUNT'),
          url: getRouteStackPath('PROFILE', 'PROFILE_PAGE'),
        },
        {
          active: true,
          title: translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.TITLE'),
          url: getRouteStackPath('PROFILE', 'MULTI_ACCESS'),
        },
        {
          active: true,
          title: translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.ACCOUNT.TITLE'),
          url: getRouteStackPath('PROFILE', 'ACCOUNT_MULTI_ACCESS'),
          state: {
            account: navigate?.location?.state?.account,
          },
        },
        {
          active: true,
          title: translate(
            'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.CREDENTIALS.TITLE'
          ),
          url: `${getRouteStackPath('PROFILE', 'CREDENTIAL_MULTI_ACCESS')}`,
          state: {
            account: navigate?.location?.state?.account,
            credential: navigate?.location?.state?.credential,
          },
        },
        {
          active: false,
          title: translate(
            'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.TITLE'
          ),
        },
      ];
    }

    return [
      {
        active: true,
        title: translate('PAGES.HOME.PAY.BREADCRUMB.HOME'),
        url: getRouteStackPath('HOME', 'HOME_PAGE'),
      },
      {
        active: true,
        title: translate('PAGES.MY_ACCOUNT.TABS.ACCOUNT'),
        url: getRouteStackPath('PROFILE', 'PROFILE_PAGE'),
      },
      {
        active: true,
        title: translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.TITLE'),
        url: getRouteStackPath('PROFILE', 'MULTI_ACCESS'),
      },
      {
        active: false,
        title: translate(
          'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.TITLE'
        ),
      },
    ];
  };

  return (
    <>
      <AdvancedHeader
        title={translate('APPLICATION.TITLE')}
        showBreadCrumbs
        breadCrumbs={handleBreadcrumbItems()}
      />
      <div className="multiaccess-page">
        <AdvancedPanelContentHeader
          testId="permissions-multiaccess-header"
          title={translate(
            'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.TYPE'
          )}
          description={translate(
            'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.OPTIONS'
          )}
          goBackAction={() => navigate.back()}
        />
        <Grid xs={12} container spacing={2}>
          {hasMatchingPermissions(PermissionsCardType.TRANSFER) && (
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              lg={4}
              xl={3}
              alignItems={'stretch'}
            >
              <PermissionsCard
                title={translate(
                  'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.TRANSFER'
                )}
                type={PermissionsCardType.TRANSFER}
                onChange={onChange}
                selectedRoles={selectedRoles}
              />
            </Grid>
          )}
          {pix.enabled && hasMatchingPermissions(PermissionsCardType.PIX) ? (
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              lg={4}
              xl={3}
              alignItems={'stretch'}
            >
              <PermissionsCard
                title={translate(
                  'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.PIX'
                )}
                type={PermissionsCardType.PIX}
                onChange={onChange}
                selectedRoles={selectedRoles}
              />
            </Grid>
          ) : null}
          {myAccount.billetLimit &&
          hasMatchingPermissions(PermissionsCardType.BILLET) ? (
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              lg={4}
              xl={3}
              alignItems={'stretch'}
            >
              <PermissionsCard
                title={translate('ENUMERATORS.TRANSACTION_TYPE.BILLET.TEXT')}
                type={PermissionsCardType.BILLET}
                onChange={onChange}
                selectedRoles={selectedRoles}
              />
            </Grid>
          ) : null}
          {transfer.ted && hasMatchingPermissions(PermissionsCardType.TED) ? (
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              lg={4}
              xl={3}
              alignItems={'stretch'}
            >
              <PermissionsCard
                title={translate('ENUMERATORS.TRANSACTION_TYPE.TED.TEXT')}
                type={PermissionsCardType.TED}
                onChange={onChange}
                selectedRoles={selectedRoles}
              />
            </Grid>
          ) : null}
          {transfer.p2p && hasMatchingPermissions(PermissionsCardType.P2P) ? (
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              lg={4}
              xl={3}
              alignItems={'stretch'}
            >
              <PermissionsCard
                title={translate('ENUMERATORS.TRANSACTION_TYPE.P2P.TEXT')}
                type={PermissionsCardType.P2P}
                onChange={onChange}
                selectedRoles={selectedRoles}
              />
            </Grid>
          ) : null}
          {cards.enabled &&
          hasMatchingPermissions(PermissionsCardType.CARDS) ? (
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              lg={4}
              xl={3}
              alignItems={'stretch'}
            >
              <PermissionsCard
                title={translate(
                  'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.CARDS'
                )}
                type={PermissionsCardType.CARDS}
                onChange={onChange}
                selectedRoles={selectedRoles}
              />
            </Grid>
          ) : null}
          {hasMatchingPermissions(PermissionsCardType.PROFILE) && (
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              lg={4}
              xl={3}
              alignItems={'stretch'}
            >
              <PermissionsCard
                title={translate(
                  'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.PROFILE'
                )}
                type={PermissionsCardType.PROFILE}
                onChange={onChange}
                selectedRoles={selectedRoles}
              />
            </Grid>
          )}
          {payment.accountPayable &&
          hasMatchingPermissions(PermissionsCardType.PAYABLE) ? (
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              lg={4}
              xl={3}
              alignItems={'stretch'}
            >
              <PermissionsCard
                title={translate(
                  'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.PAYABLE'
                )}
                type={PermissionsCardType.PAYABLE}
                onChange={onChange}
                selectedRoles={selectedRoles}
              />
            </Grid>
          ) : null}
          {payment.accountReceivable &&
          hasMatchingPermissions(PermissionsCardType.RECEIVABLE) ? (
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              lg={4}
              xl={3}
              alignItems={'stretch'}
            >
              <PermissionsCard
                title={translate(
                  'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.RECEIVABLE'
                )}
                type={PermissionsCardType.RECEIVABLE}
                onChange={onChange}
                selectedRoles={selectedRoles}
              />
            </Grid>
          ) : null}
          {me.account.type === AccountTaxIdentifierType.CORPORATE &&
          payslip.enabled &&
          hasMatchingPermissions(PermissionsCardType.PAYSLIP) ? (
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              lg={4}
              xl={3}
              alignItems={'stretch'}
            >
              <PermissionsCard
                title={translate(
                  'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.PAYSLIP'
                )}
                type={PermissionsCardType.PAYSLIP}
                onChange={onChange}
                selectedRoles={selectedRoles}
              />
            </Grid>
          ) : null}
          {employees.enabled &&
          allowEmployees &&
          hasMatchingPermissions(PermissionsCardType.EMPLOYEES) ? (
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              lg={4}
              xl={3}
              alignItems={'stretch'}
            >
              <PermissionsCard
                title={translate(
                  'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.EMPLOYEES'
                )}
                type={PermissionsCardType.EMPLOYEES}
                onChange={onChange}
                selectedRoles={selectedRoles}
              />
            </Grid>
          ) : null}
          {me.account.type === AccountTaxIdentifierType.CORPORATE && (
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              lg={4}
              xl={3}
              alignItems={'stretch'}
            >
              <PermissionsCard
                title={translate(
                  'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.GENERAL'
                )}
                type={PermissionsCardType.ALL}
                onChange={() => onChange('my-account')}
                selectedRoles={selectedRoles}
              >
                <AdvancedCheckbox
                  label={translate(
                    'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.ALL_PERMISSIONS'
                  )}
                  value={allRolesSelected}
                  onChange={() => onChange('my-account')}
                />
              </PermissionsCard>
            </Grid>
          )}
        </Grid>
        <div className="multiaccess-page__buttons">
          <AdvancedButton
            size="large"
            text={translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.BUTTON')}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </>
  );
};

export default PermissionsMultiAccess;
